import _ from "lodash";
import { buildUniqueId, getUrnFromArtefact } from "../../../utils/sdmxJson";

export const HIERARCHY_FORM_MODE_READ = "HIERARCHY_FORM_MODE_READ";
export const HIERARCHY_FORM_MODE_EDIT = "HIERARCHY_FORM_MODE_EDIT";
export const HIERARCHY_FORM_MODE_CREATE = "HIERARCHY_FORM_MODE_CREATE";

export function isCodelistRootId(uniqueId) {
  return uniqueId.split("@").length === 3;
}

export function isHCLRootId(uniqueId) {
  return uniqueId.indexOf("@") === -1;
}

export function getAliasRefFromUniqueId(uniqueId) {
  let ids = uniqueId.split("@");
  return `${ids[1]}@${ids[0]}@${ids[2]}`;
}

export function getUniqueIdFromAlias(uniqueId) {
  let ids = uniqueId.split("@");
  return `${ids[1]}@${ids[0]}@${ids[2]}`;
}

export function getCodelistNodeId(agencyID, id, version) {
  return buildUniqueId(buildUniqueId(agencyID, id), version.replace(".", ""));
}

export function getCodelistAliasRef(agencyID, id, version) {
  return buildUniqueId(buildUniqueId(id, agencyID), version.replace(".", ""));
}

export function getHclNodeHierarchy(node) {
  return node.uniqueId.split("@")[0];
}

function getHCLId(uniqueId){
  return uniqueId.split("@")[0];
}

export function getExistingNodesIds(children, copyTree, idKey) {
  let existingNodesIds = [];

  if (children.length > 0) {
    const childrenKeys = children.map((node) => node[idKey]);
    const copyKeys = copyTree.map((node) => node[idKey]);
    let existingKeys = copyKeys.filter((k) => childrenKeys.includes(k));
    existingNodesIds = children
      .filter((node) => existingKeys.includes(node[idKey]))
      .map((node) => node.id);
  }
  return existingNodesIds;
}

export function getTreeWithUniqueId(
  arr,
  prefix,
  codelistAliasRef,
  childrenKey,
  dictCodes
) {
  const mappedArr = {};
  const tree = [];

  const validKeys = [
    "id",
    "code",
    "codeID",
    "codelistAliasRef",
    "uniqueId",
    "name",
    "names",
    "description",
    "descriptions",
    childrenKey,
    "uniqueId",
    "parent",
    "links",
  ];

  // first map the nodes of the array to an object -> create a hash table.
  arr.forEach((el) => {
    Object.keys(el).forEach((key) => validKeys.includes(key) || delete el[key]);
    el[childrenKey] = [];   
    mappedArr[el.id] = el;
    el.codelistAliasRef = codelistAliasRef;
    let key = buildUniqueId(codelistAliasRef, el.id);
    dictCodes[key] = el.name;
    el.uniqueId = buildUniqueId(prefix,el.id);    
  });

  arr.forEach((el) => {
    if (el.parent) {
      // if the element is not at the root level, add it to its parent array of children.    
      mappedArr[el.parent][childrenKey].push(el);
    } else {
      // if the element is at the root level, add it to first level elements array.
      el.parent = prefix;
      tree.push(el);
    }
  });

  return tree;
}

export function getCodelistTreeNode(codelist, childrenKey, dictCodes) {
  let nodeId = getCodelistNodeId(
    codelist.agencyID,
    codelist.id,
    codelist.version
  );
  let codelistAliasRef = getCodelistAliasRef(
    codelist.agencyID,
    codelist.id,
    codelist.version
  );

  let itemsTree = getTreeWithUniqueId(
    codelist.codes || [],
    nodeId,
    codelistAliasRef,
    childrenKey,
    dictCodes
  );

  let includedCodelist = {
    id: nodeId,
    uniqueId: nodeId,
    name: codelist.name,
    links: codelist.links,
    isRoot: true,
    codelistAliasRef: codelistAliasRef,
    [childrenKey]: itemsTree,
  };

  return includedCodelist;
}

export function findReferencedItems(arr, codelistAliasRef, referencedItems) {
  (arr || []).forEach((el) => {
    if (el.codelistAliasRef === codelistAliasRef) {
      const hierarchyId = getHCLId(el.uniqueId);
      if (referencedItems.hasOwnProperty(hierarchyId)) {
        referencedItems[hierarchyId].push(el.uniqueId);
      } else {
        referencedItems[hierarchyId] = [el.uniqueId];
      }
    }
    findReferencedItems(
      el.hierarchicalCodes || [],
      codelistAliasRef,
      referencedItems
    );
  });
}

export function deleteItemsFromHCLHierarchies(hcl, items) {
  if ((items || []).length === 0) {
    return;
  }
  hcl.hierarchies = hcl.hierarchies || [];
  let item = items[0];
  if (isHCLRootId(item)) {
    hcl.hierarchies = hcl.hierarchies.filter((h) => !items.includes(h.id));
  } else {
    hcl.hierarchies.forEach((h) => {
      h.hierarchicalCodes = deleteCodesFromHierarchy(
        h.hierarchicalCodes,
        items
      );
    });
  }
  }    

function deleteCodesFromHierarchy(codes, items) {
  if ( (codes || []).length === 0) {
    return [];
  }

  let result = codes.filter((hc) => !items.includes(hc.uniqueId));
  if ((result || []).length > 0) {
    result.forEach((c) => {
      c.hierarchicalCodes = deleteCodesFromHierarchy(
        c.hierarchicalCodes,
        items
      );
    });
  }
 
  return result;
}

export function getToPasteTree(nodes, idKey, childrenKey, destNode) {
  let nodesCopy = _.cloneDeep(nodes);

  const parentsMap = {};
  const tree = [];

  nodesCopy.forEach((node) => {
    node[childrenKey] = [];
    node.code = getUrnFromArtefact(node);
    node.codeID = node.id;
    node.codelistAliasRef = node.codelistAliasRef;
    delete node.links;
    
    parentsMap[node.id] = node;
  });

  nodesCopy.forEach((node) => {
    let directParent =
      node.parent && parentsMap[node.parent] ? parentsMap[node.parent] : null;
    if (directParent) {
      let uniqueid = buildUniqueId(node.parent, node.id);
      let parentNode = directParent;
      while (parentNode && parentNode.parent) {
        if (parentsMap[parentNode.parent]) {
          uniqueid = buildUniqueId(parentNode.parent, uniqueid);
        }        
        parentNode = parentsMap[parentNode.parent];
      }
      node[idKey] = buildUniqueId(destNode[idKey], uniqueid);
      node.newParent = directParent[idKey];
      parentsMap[node.parent][childrenKey].push(node);
    } else {
      node[idKey] = buildUniqueId(destNode[idKey], node.id);
      node.newParent = destNode[idKey];
      tree.push(node);
    }
  });
        
  Object.values(parentsMap).forEach((node) => {
    node.parent = node.newParent;
    delete node.newParent;
  });

  return tree;
}

export function updateParentOfNodes(nodes, parentNodeId, childrenKey) {
  if ((nodes || []).length === 0) {
    return [];
  }
  const updatedNodes = _.cloneDeep(nodes);
  updatedNodes.forEach((node) => {
    node.parent = parentNodeId;
    node.uniqueId = buildUniqueId(parentNodeId, node.id);
    node[childrenKey] = updateParentOfNodes(
      node[childrenKey],
      node.uniqueId,
      childrenKey
    );
  }) ;

  return updatedNodes;
}

export function hierarchicalCodelistHasLevelBasedHierarchies(
  hierarchicalCodelist
) {
  const hierarchies = hierarchicalCodelist.hierarchies || [];
  return hierarchies.find((h) => h.leveled) !== undefined;
}
